import { useAppDispatch } from 'app/hooks';
import { SelectedSystemType } from 'data/types';
import { useUpdateQuotableSystemAccessoriesMutation } from 'features/api/project-api';
import { updateSelectedSystem } from 'features/systems';
import { useUnquoteOnAccessoryChange } from 'hooks/use-unquote-on-accessory-change';
import { useCallback } from 'react';
import {
  flattenDuctedAccessorySelections,
  toDuctedAccessorySavePayload
} from 'utils/ducted-accessory-selections';
import type { DuctedAccessorySelectionsType } from 'utils/ducted-accessories-types';
import { combineProductQuantities } from 'utils/products';
import { errorModal } from 'utils/ui';

export function useDuctedAccessoriesWriter(system: SelectedSystemType) {
  const dispatch = useAppDispatch();
  const { unquoteDuctless } = useUnquoteOnAccessoryChange();
  const [updateQuotableSystemAccessories] =
    useUpdateQuotableSystemAccessoriesMutation();

  const writeSelections = useCallback(
    (next: DuctedAccessorySelectionsType, persist: boolean) => {
      const condensers = system.configuration.condensers.map((item, index) => ({
        ...item,
        selectedAccessories: combineProductQuantities([
          ...(index === 0 ? next.system : []),
          ...(next.units[item.uniqueId] ?? [])
        ]),
        noAccessoriesNeeded: false
      }));
      dispatch(
        updateSelectedSystem({
          uniqueId: system.uniqueId,
          quotableAccessories: flattenDuctedAccessorySelections(next),
          noAccessoriesNeeded: false,
          configuration: { ...system.configuration, condensers }
        })
      );
      if (!persist) return;
      unquoteDuctless(system);
      updateQuotableSystemAccessories({
        uniqueId: system.uniqueId,
        accessories: toDuctedAccessorySavePayload(next)
      })
        .unwrap()
        .catch(() => errorModal('Failed to save accessories.'));
    },
    [dispatch, system, unquoteDuctless, updateQuotableSystemAccessories]
  );

  const applySelections = useCallback(
    (next: DuctedAccessorySelectionsType) => writeSelections(next, true),
    [writeSelections]
  );

  return { writeSelections, applySelections };
}
